import {
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemActions,
  Button,
  Skeleton,
  Alert,
  Flex,
  Text,
} from '@kognitos/lattice';
import {
  useRetrieveWorkspaceBook,
  useRetrieveWorkspaceBookConnections,
} from '@/generated/hooks/bdk/v2/services/library';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import { useTranslation } from 'react-i18next';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import type { IDraftPublishDialogConnectionItemProps } from '../types';

function DraftPublishDialogConnectionItem({
  credentialInfo,
  onSelectConnectionClick,
  onAddProdConnection,
  onAddNewConnection,
}: IDraftPublishDialogConnectionItemProps) {
  const { t: tDrafts } = useTranslation('drafts');
  const { t: tIntegrations } = useTranslation('integrations');

  const versionText = credentialInfo.book_version;
  const displayName = credentialInfo.book;

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    data: appData,
    isLoading: isAppLoading,
    isError: isAppError,
  } = useRetrieveWorkspaceBook(
    organizationId,
    workspaceId,
    credentialInfo.book,
    credentialInfo.book_version,
    {
      query: {
        enabled:
          !!organizationId &&
          !!workspaceId &&
          !!credentialInfo.book &&
          !!credentialInfo.book_version,
      },
    },
  );

  const hasDraftConnection = Boolean(credentialInfo.connection_id);

  const { data: existingConnectionsData } = useRetrieveWorkspaceBookConnections(
    organizationId,
    workspaceId,
    credentialInfo.book,
    credentialInfo.book_version,
    undefined,
  );

  const hasConnections = Boolean(
    existingConnectionsData?.data?.book_connections?.length,
  );

  if (isAppError) {
    return (
      <Alert variant="destructive">
        {tIntegrations('integrations.error.retrievingIntegrationDetails')}
      </Alert>
    );
  }

  if (isAppLoading || !appData?.data.book) {
    return <Skeleton className="w-full h-20" />;
  }

  const integration = appData?.data.book;

  const handleActionClick = () => {
    if (hasDraftConnection) {
      onAddProdConnection(integration);
    } else if (hasConnections) {
      onSelectConnectionClick(integration);
    } else {
      onAddNewConnection(integration);
    }
  };

  const connectionName =
    existingConnectionsData?.data?.book_connections?.find(
      connection => connection.connection_id === credentialInfo.connection_id,
    )?.connection_name || '';

  const getDescription = () => {
    if (hasDraftConnection && credentialInfo.connection_id) {
      return `${connectionName}`;
    }
    if (hasConnections) {
      return tDrafts(
        'drafts.publishDialog.connectionDescription.selectExistingOrAddNew',
      );
    }
    return tDrafts('drafts.publishDialog.connectionDescription.addNew');
  };

  return (
    <Item variant="outline" className="w-full gap-3">
      <ItemMedia variant="default" className="pt-0.5">
        <img
          src={convertToDataUrl(integration.icon)}
          alt={`${displayName} icon`}
          className="w-4 h-4"
        />
      </ItemMedia>
      <ItemContent>
        <ItemTitle>
          {integration.display_name} v{versionText}
        </ItemTitle>
        <Flex gap={2} align="center">
          <Text level="small" color="muted">
            {getDescription()}
          </Text>
        </Flex>
      </ItemContent>
      <ItemActions>
        <Button variant="outline" size="sm" onClick={handleActionClick}>
          {hasConnections || hasDraftConnection
            ? tDrafts('drafts.publishDialog.actions.configure')
            : tDrafts('drafts.publishDialog.actions.connect')}
        </Button>
      </ItemActions>
    </Item>
  );
}

export default DraftPublishDialogConnectionItem;
